import React, { useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import QrCodeScanner from '../QrCodeScanner';
import './TicketValidatorPage.css';

function TicketValidatorPage() {
    const [result, setResult] = useState('');
    const [showScanner, setShowScanner] = useState(true);
    const [debugInfo, setDebugInfo] = useState('');
    const [testMode, setTestMode] = useState(false);
    const [manualTicketId, setManualTicketId] = useState('');

    const handleScanSuccess = async (decodedText) => {
        setShowScanner(false);
        
        // Debug: Log the raw scanned text
        console.log('Raw QR data:', decodedText);
        setDebugInfo(`Raw QR: ${decodedText.substring(0, 100)}...`);
        
        try {
            // Try to parse as JSON
            let ticketId;
            try {
                const qrData = JSON.parse(decodedText);
                console.log('Parsed QR data:', qrData);
                ticketId = qrData.ticketId;
                setDebugInfo(prev => prev + `\nParsed ticketId: ${ticketId}`);
            } catch (jsonError) {
                // If not JSON, treat the whole string as ticketId
                console.log('QR is not JSON, treating as plain ticketId');
                ticketId = decodedText.trim();
                setDebugInfo(prev => prev + `\nUsing raw text as ticketId: ${ticketId}`);
            }

            if (!ticketId) {
                setResult('❌ Invalid QR code: No ticketId found');
                return;
            }

            console.log('Looking up ticket:', ticketId);
            const ticketRef = doc(db, 'tickets', ticketId);
            
            let ticketSnap;
            try {
                ticketSnap = await getDoc(ticketRef);
                console.log('Firestore query result:', ticketSnap.exists() ? 'Found' : 'Not found');
            } catch (firestoreError) {
                console.error('Firestore error:', firestoreError);
                if (firestoreError.code === 'permission-denied') {
                    setResult('❌ Permission denied - Check Firestore security rules');
                    setDebugInfo(prev => prev + '\nFirestore permission denied!');
                } else {
                    setResult(`❌ Database error: ${firestoreError.message}`);
                }
                return;
            }

            if (!ticketSnap.exists()) {
                setResult(`❌ Ticket not found: ${ticketId}`);
                setDebugInfo(prev => prev + '\nTicket does not exist in database');
                return;
            }

            const ticketData = ticketSnap.data();
            console.log('Ticket data:', ticketData);

            if (ticketData.validated) {
                let validatedDate;
                if (ticketData.validatedAt && ticketData.validatedAt.toDate) {
                    validatedDate = ticketData.validatedAt.toDate();
                } else {
                    validatedDate = ticketData.validatedAt;
                }
                const dateString = validatedDate ? validatedDate.toLocaleString() : 'unknown time';
                setResult(`⚠️ Ticket already validated at ${dateString}`);
                return;
            }

            await updateDoc(ticketRef, {
                validated: true,
                validatedAt: new Date(),
            });

            setResult(`✅ VALID - ${ticketData.customerName || ticketData.customerEmail || 'Guest'}`);
            setDebugInfo(prev => prev + '\nTicket validated successfully!');
        } catch (error) {
            console.error("Validation Error:", error);
            setResult(`❌ Error: ${error.message}`);
            setDebugInfo(prev => prev + `\nError: ${error.message}`);
        }
    };

    const handleManualValidation = async () => {
        if (!manualTicketId.trim()) {
            setResult('❌ Please enter a ticket ID');
            return;
        }
        setShowScanner(false);
        await handleScanSuccess(manualTicketId.trim());
    };

    const handleScanFailure = (error) => {
        console.error('QR scan error:', error);
        setResult('Failed to scan QR code.');
        setShowScanner(false);
    };

    const handleScanAgain = () => {
        setResult('');
        setShowScanner(true);
    };

    return (
        <div className="page validator-page">
            <div className="validator-container">
                <div className="card">
                    <div className="card-header">
                        <h2>Ticket Scanner</h2>
                        <button 
                            onClick={() => setTestMode(!testMode)} 
                            className="btn btn-secondary"
                            style={{ marginLeft: '10px', fontSize: '14px' }}
                        >
                            {testMode ? 'Hide' : 'Show'} Test Mode
                        </button>
                    </div>
                    <div className="card-body">
                        {testMode && (
                            <div style={{ 
                                marginBottom: '20px', 
                                padding: '15px', 
                                backgroundColor: '#f0f0f0', 
                                borderRadius: '5px' 
                            }}>
                                <h4>Manual Ticket ID Test</h4>
                                <p style={{ fontSize: '12px', color: '#666' }}>
                                    Example: TICKET-MAXuXj94C6X3yFuXeyuU-1756650662047-G59FCZ
                                </p>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <input
                                        type="text"
                                        value={manualTicketId}
                                        onChange={(e) => setManualTicketId(e.target.value)}
                                        placeholder="Enter ticket ID"
                                        style={{ 
                                            flex: 1, 
                                            padding: '8px', 
                                            border: '1px solid #ccc', 
                                            borderRadius: '4px' 
                                        }}
                                    />
                                    <button 
                                        onClick={handleManualValidation}
                                        className="btn btn-primary"
                                    >
                                        Validate
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        {showScanner ? (
                            <div className="qr-reader-container">
                                <QrCodeScanner
                                    onScanSuccess={handleScanSuccess}
                                    onScanFailure={handleScanFailure}
                                />
                            </div>
                        ) : (
                            <div>
                                <div className={`alert ${result.includes('✅') ? 'alert-success' : result.includes('⚠️') ? 'alert-warning' : 'alert-danger'}`}>
                                    {result}
                                </div>
                                
                                {debugInfo && (
                                    <div style={{ 
                                        marginTop: '15px', 
                                        padding: '10px', 
                                        backgroundColor: '#f8f9fa', 
                                        border: '1px solid #dee2e6', 
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontFamily: 'monospace',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all'
                                    }}>
                                        <strong>Debug Info:</strong><br/>
                                        {debugInfo}
                                    </div>
                                )}
                                
                                <button 
                                    onClick={() => {
                                        handleScanAgain();
                                        setDebugInfo('');
                                        setManualTicketId('');
                                    }} 
                                    className="btn btn-primary"
                                    style={{ marginTop: '15px' }}
                                >
                                    Scan Again
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TicketValidatorPage;
